import { Search, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { apiClient } from "@/shared/services/api";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { useAuth } from "@/shared/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { SidebarTrigger } from "@/shared/components/ui/sidebar";
import { NotificationBell } from "@/components/NotificationBell";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { X } from "lucide-react";

interface Customer {
  id: number;
  name: string;
  phone_number: string;
  email?: string | null;
  project?: string | null;
}

export function Header() {
  const navigate = useNavigate();
  const { usage, loading: subscriptionLoading } = useSubscription();
  const { logout, user: authUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [userName, setUserName] = useState("User");
  const [userInitials, setUserInitials] = useState("U");
  const [userPlan, setUserPlan] = useState<'free' | 'pro' | 'team' | null>(null);
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // CRITICAL FIX: Use user from AuthContext instead of calling getUser()
  useEffect(() => {
    if (authUser) {
      const fullName = authUser.name || authUser.email || "User";
      setUserName(fullName);

      const nameParts = fullName.split(" ");
      const initials = nameParts.length > 1
        ? nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)
        : nameParts[0].charAt(0);
      setUserInitials(initials.toUpperCase());
    }
  }, [authUser]);

  // Update user plan from subscription context - matching mobile behavior
  useEffect(() => {
    // Don't update plan until subscription finishes loading
    if (subscriptionLoading) {
      setUserPlan(null); // Hide badge during loading
      return;
    }

    // Match mobile logic for showing plan type
    if (usage.subscribed || usage.isTrialActive) {
      // Determine plan type from subscription data
      const maxUsers = usage.maxUsers || 1;
      if (maxUsers > 1) {
        setUserPlan('team');
      } else {
        setUserPlan('pro');
      }
    } else {
      setUserPlan(null); // Don't show badge for free users
    }
  }, [usage.subscribed, usage.isTrialActive, usage.maxUsers, subscriptionLoading]);

  // Get badge text and color based on subscription state (matching mobile)
  const getBadgeInfo = () => {
    if (!userPlan) return null;

    // Canceled subscription - red badge (highest priority)
    if (usage.subscription_status === 'canceled') {
      return {
        text: 'Canceled',
        className: 'bg-red-100 text-red-700 hover:bg-red-100'
      };
    }

    // Paused subscription - yellow badge
    if (usage.is_paused) {
      return {
        text: 'Paused',
        className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
      };
    }

    // Trial (app) - no card, yellow badge
    if (!usage.subscribed && usage.isTrialActive && usage.trialType === 'app') {
      const daysLeft = usage.daysLeftInTrial || 15;
      return {
        text: `Trial (${daysLeft}d left)`,
        className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
      };
    }

    // Paid Trial (stripe) - card on file, green badge
    if (usage.subscribed && usage.isTrialActive && usage.trialType === 'stripe') {
      const daysLeft = usage.daysLeftInTrial || 15;
      return {
        text: `Pro Trial (${daysLeft}d until billing)`,
        className: 'bg-green-100 text-green-700 hover:bg-green-100'
      };
    }

    // Active subscription (no trial)
    if (usage.subscribed && !usage.isTrialActive) {
      return {
        text: userPlan === 'team' ? 'Team Plan' : 'Pro Plan',
        className: userPlan === 'team'
          ? 'bg-purple-100 text-purple-700 hover:bg-purple-100'
          : 'bg-blue-100 text-blue-700 hover:bg-blue-100'
      };
    }

    return null;
  };

  const badgeInfo = getBadgeInfo();

  // Search customers as user types - using backend API
  useEffect(() => {
    const searchCustomers = async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setIsSearchOpen(false);
        return;
      }

      try {
        // Use backend endpoint instead of direct Supabase call
        const response = await apiClient.get<{ ok: boolean; customers: Customer[] }>(
          `/customers/search?q=${encodeURIComponent(searchTerm)}&limit=5`
        );

        setSearchResults(response.customers || []);
        setIsSearchOpen(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      }
    };

    const debounce = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const handleCustomerClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsCustomerModalOpen(true);
    setIsSearchOpen(false);
    setSearchTerm('');
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts.length > 1
      ? parts[0][0] + parts[parts.length - 1][0]
      : parts[0][0];
  };

  const handleLogout = async () => {
    // Prevent duplicate logout calls
    if ((window as any).__LOGOUT_IN_PROGRESS__) {
      console.log('[Header] Logout already in progress, skipping...');
      return;
    }

    try {
      (window as any).__LOGOUT_IN_PROGRESS__ = true;
      console.log('[Header] Logout button clicked');

      // Use AuthContext logout method (properly handles all state cleanup)
      await logout();

      console.log('[Header] ✅ Successfully signed out via AuthContext');

      // Preserve onboarding state
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      localStorage.clear();
      if (hasSeenOnboarding) {
        localStorage.setItem('hasSeenOnboarding', hasSeenOnboarding);
      }

      // Navigate to landing page
      navigate('/', { state: { fromLogout: true }, replace: true });
    } catch (error) {
      console.error('[Header] Logout error:', error);

      // Even if logout fails, clear local state and navigate
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      localStorage.clear();
      if (hasSeenOnboarding) {
        localStorage.setItem('hasSeenOnboarding', hasSeenOnboarding);
      }

      navigate('/', { state: { fromLogout: true }, replace: true });
    } finally {
      // CRITICAL: Always reset the flag to prevent permanent blocking
      (window as any).__LOGOUT_IN_PROGRESS__ = false;
      console.log('[Header] Logout process completed, flag reset');
    }
  };

  return (
    <header className="h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <div className="flex h-full items-center justify-between px-2 md:px-6 overflow-hidden">
        <div className="flex items-center space-x-1 md:space-x-4 flex-shrink-0">
          {isMobile && <SidebarTrigger className="h-8 w-8 p-1" />}

          {/* Search with Popover */}
          <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
            <PopoverTrigger asChild>
              <div className="relative max-w-md hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                <Input
                  placeholder={t("header.searchContacts")}
                  className="pl-9 w-80"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => {
                    if (searchTerm.trim() && searchResults.length > 0) {
                      setIsSearchOpen(true);
                    }
                  }}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="w-80 p-0 bg-background border shadow-lg"
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {searchResults.length > 0 ? (
                <div className="max-h-80 overflow-y-auto">
                  {searchResults.map((customer) => (
                    <div
                      key={customer.id}
                      className="p-3 hover:bg-accent cursor-pointer border-b last:border-b-0"
                      onClick={() => handleCustomerClick(customer)}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs bg-primary/10">
                            {getInitials(customer.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{customer.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {customer.phone_number}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No customers found
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center space-x-1 md:space-x-4 flex-shrink-0 min-w-0">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className={`flex items-center h-auto p-1 md:p-2 ${isMobile ? 'space-x-1 max-w-[120px]' : 'space-x-2'}`}>
                <Avatar className="h-6 w-6 md:h-8 md:w-8 flex-shrink-0">
                  <AvatarFallback className="text-xs md:text-sm">{userInitials}</AvatarFallback>
                </Avatar>
                {!isMobile && (
                  <div className="flex flex-col items-start text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{userName}</span>
                      {badgeInfo && (
                        <Badge
                          variant="secondary"
                          className={`text-[10px] px-1.5 py-0 ${badgeInfo.className}`}
                        >
                          {badgeInfo.text}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                {isMobile && (
                  <span className="font-medium text-xs truncate max-w-[60px]">{userName}</span>
                )}
                <ChevronDown className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background border">
              <DropdownMenuLabel>{t("header.myAccount")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                {t("header.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                {t("header.settings")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/support')} className="cursor-pointer">
                {t("nav.support")}
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                {t("header.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Customer Detail Modal - Compact Design */}
      <Dialog open={isCustomerModalOpen} onOpenChange={setIsCustomerModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-4 py-2">
              {/* Avatar & Name */}
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="text-lg bg-primary/10">
                    {getInitials(selectedCustomer.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold">{selectedCustomer.name}</h3>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 border-t pt-4">
                <div>
                  <label className="text-xs text-muted-foreground">Phone</label>
                  <p className="text-sm font-medium">{selectedCustomer.phone_number}</p>
                </div>
                {selectedCustomer.email && (
                  <div>
                    <label className="text-xs text-muted-foreground">Email</label>
                    <p className="text-sm font-medium">{selectedCustomer.email}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 border-t pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsCustomerModalOpen(false);
                    navigate(`/contacts?customer=${selectedCustomer?.id || ''}`);
                  }}
                >
                  View Full Profile
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </header>
  );
}
